import { PLATFORM_LEGAL_PATHS, RETENTION, type PublicLegalOutput } from '@garage/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useBrand } from '../shared/auth.js';
import { api } from '../shared/api.js';
import { PlatformLogo } from './PlatformMark.js';
import { PLATFORM_LEGAL_LINKS } from './platform.js';

/**
 * Pages légales **de la plateforme** (vague 7) : `/mentions-legales` et `/confidentialite`, publiques,
 * sans session ni marque de garage, servies sur l'espace garage comme sur la page de suivi.
 * Identité de l'éditeur et adresses : `public.legal` (configuration du serveur), jamais écrites ici.
 * Texte source, relu par un juriste : `docs/legal/` (même contenu que `docs/legal/vitrine/*.html`).
 * À ne pas confondre avec la notice **du garage** (`/confidentialite/<clé>`, `portal/pages/PrivacyPage.tsx`).
 */

const TODO = 'à compléter';

/** « 5 octobre 2026 » pour une date AAAA-MM-JJ. */
function dayLabel(day: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    .formatToParts(new Date(`${day}T12:00:00Z`))
    .map((p) => (p.type === 'day' && p.value === '1' ? '1er' : p.value))
    .join('');
}

function useLegal() {
  return useQuery({ queryKey: ['public.legal'], queryFn: () => api('public.legal'), staleTime: 5 * 60_000 });
}

/** Liens légaux de la plateforme (pied de page) : réutilisés par le portail et le compte de l'équipe. */
export function PlatformLegalLinks({ className }: { className?: string }) {
  return (
    <nav aria-label="Informations légales" className={className}>
      <ul className="flex flex-wrap items-center justify-center gap-x-1">
        {PLATFORM_LEGAL_LINKS.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              className="inline-flex min-h-(--ctl-sm) items-center rounded-lg px-2.5 text-sm text-ink-3 underline decoration-line-strong underline-offset-4 transition-colors hover:text-ink hover:decoration-ink-3 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function LegalShell({ title, legal, children }: { title: string; legal: PublicLegalOutput | null; children: ReactNode }) {
  const product = legal?.productName ?? '';
  useBrand(null, product ? `${title} · ${product}` : title);
  return (
    <div className="platform-scope min-h-dvh bg-surface text-ink">
      <header className="platform-panel border-b-[3px] border-platform-accent-on-panel">
        <div className="mx-auto flex h-16 max-w-2xl items-center px-[var(--page-x)]">
          <PlatformLogo name={legal ? product : null} tone="dark" width={156} />
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-[var(--page-x)] pt-6 pb-10 text-base leading-relaxed text-ink-2">
        <h1 className="platform-title text-[2.25rem] text-balance text-ink sm:text-[2.75rem]">{title}</h1>
        {children}
      </main>
      <footer className="border-t border-line px-[var(--page-x)] pt-3 pb-[calc(0.75rem+var(--safe-bottom))]">
        <PlatformLegalLinks />
      </footer>
    </div>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-8 mb-2 text-xl font-semibold tracking-[-0.01em] text-ink">{children}</h2>;
}

function P({ children }: { children: ReactNode }) {
  return <p className="mt-2">{children}</p>;
}

function Ul({ children }: { children: ReactNode }) {
  return <ul className="mt-2 list-disc space-y-1 pl-5">{children}</ul>;
}

function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-ink">{children}</strong>;
}

function Mail({ email }: { email: string | null }) {
  if (!email) return <>{TODO}</>;
  return (
    <a href={`mailto:${email}`} className="font-medium text-ink underline underline-offset-2">
      {email}
    </a>
  );
}

/** Chargement ou échec : la page garde son titre, sans jamais afficher de valeur inventée. */
function Pending({ failed }: { failed: boolean }) {
  return failed ? (
    <p role="alert" className="mt-4 text-ink-2">
      Ces informations ne peuvent pas être chargées pour le moment. Réessayez dans un instant.
    </p>
  ) : (
    <p role="status" className="mt-4 text-sm font-medium text-ink-3">
      Chargement…
    </p>
  );
}

const editorName = (l: PublicLegalOutput) => l.editor.legalName ?? TODO;
const editorLabel = (l: PublicLegalOutput) =>
  l.editor.tradeName ? `${editorName(l)} (${l.editor.tradeName})` : editorName(l);

// ─── Mentions légales ───────────────────────────────────────────────────────

export function LegalNoticePage() {
  const q = useLegal();
  const l = q.data ?? null;
  return (
    <LegalShell title="Mentions légales" legal={l}>
      {l ? <LegalNoticeText l={l} /> : <Pending failed={q.isError} />}
    </LegalShell>
  );
}

export function LegalNoticeText({ l }: { l: PublicLegalOutput }) {
  const e = l.editor;
  return (
    <>
      <p className="mt-1 text-sm text-ink-3">Dernière mise à jour : {dayLabel(l.updatedAt)}</p>

      <H2>Éditeur</H2>
      <Ul>
        <li>
          Éditeur : <Strong>{editorName(l)}</Strong>
          {e.legalForm ? `, ${e.legalForm}` : ''}
        </li>
        <li>Nom commercial : {e.tradeName ?? TODO}</li>
        <li>Adresse : {e.address?.replace(/\n/g, ', ') ?? TODO}</li>
        <li>Numéro SIREN : {e.siren ?? 'en cours d’attribution'}</li>
        {e.phone ? <li>Téléphone : {e.phone}</li> : null}
        <li>
          Contact : <Mail email={e.contactEmail} />
        </li>
        <li>Directeur de la publication : {e.publicationDirector ?? TODO}</li>
      </Ul>

      <H2>Services concernés</H2>
      <P>
        {l.productName} est un service de suivi d’atelier proposé aux garages. Ces mentions s’appliquent à :
      </P>
      <Ul>
        {l.sites.website ? <li>{l.sites.website} : site de présentation ;</li> : null}
        <li>
          {l.sites.app} : espace des garages ({l.productNamePro}) ;
        </li>
        <li>{l.sites.portal} : page de suivi des clients des garages ;</li>
        <li>{l.sites.mailDomain} : domaine utilisé uniquement pour l’envoi des e-mails.</li>
      </Ul>

      <H2>Hébergement et prestataires techniques</H2>
      <Ul>
        <li>
          Hébergement de l’application et des données : <Strong>OVH SAS</Strong>, 2 rue Kellermann, 59100 Roubaix, France. Serveur situé en
          France.
        </li>
        <li>
          Réseau de diffusion et protection du trafic : <Strong>Cloudflare, Inc.</Strong>, 101 Townsend Street, San Francisco, CA 94107,
          États-Unis.
        </li>
        <li>
          Envoi des e-mails : <Strong>Sendinblue SAS (Brevo)</Strong>, France.
        </li>
      </Ul>

      <H2>Propriété intellectuelle</H2>
      <P>
        Le logiciel {l.productName}, sa présentation et ses textes génériques appartiennent à {editorName(l)}. Toute reproduction sans
        autorisation est interdite.
      </P>
      <P>
        Les noms, logos et couleurs des garages affichés dans le service appartiennent à chaque garage. Les photos des véhicules sont prises
        par les garages, sous leur responsabilité.
      </P>

      <H2>Responsabilité</H2>
      <P>
        Chaque garage utilisateur est une entreprise indépendante de l’éditeur. Les informations d’une intervention (photos, propositions de
        travaux, montants, dates, messages) sont établies par le garage, qui en est seul responsable.
      </P>
      <P>
        Une proposition de travaux n’est acceptée que par une décision explicite du client ; ni le silence ni un message libre ne valent
        accord.
      </P>

      <H2>Données personnelles et cookies</H2>
      <P>
        Voir la{' '}
        <a href={PLATFORM_LEGAL_PATHS.privacy} className="font-medium text-ink underline underline-offset-2">
          politique de confidentialité
        </a>{' '}
        de {l.productName}. Le service n’utilise que des cookies strictement nécessaires à son fonctionnement.
      </P>

      <H2>Droit applicable</H2>
      <P>Ces mentions sont soumises au droit français.</P>
    </>
  );
}

// ─── Politique de confidentialité de la plateforme ─────────────────────────

export function PlatformPrivacyPage() {
  const q = useLegal();
  const l = q.data ?? null;
  return (
    <LegalShell title="Politique de confidentialité" legal={l}>
      {l ? <PlatformPrivacyText l={l} /> : <Pending failed={q.isError} />}
    </LegalShell>
  );
}

const R = RETENTION;

export function PlatformPrivacyText({ l }: { l: PublicLegalOutput }) {
  const ed = editorLabel(l);
  const mail = <Mail email={l.editor.privacyEmail} />;
  return (
    <>
      <p className="mt-1 text-sm text-ink-3">Dernière mise à jour : {dayLabel(l.updatedAt)}</p>
      <p className="mt-4">
        Cette page explique comment {ed}, éditeur de {l.productName}, traite les données personnelles dans le service : celles des équipes des
        garages, et celles des clients des garages, qu’il héberge pour le compte de chaque garage.
      </p>

      <H2>Qui est responsable</H2>
      <Ul>
        <li>
          <Strong>Comptes des équipes des garages</Strong> (espace {l.productNamePro}) : {ed} est responsable de traitement.
        </li>
        <li>
          <Strong>Données des clients des garages</Strong> (fiches, véhicules, interventions, photos, messages, page de suivi) : chaque{' '}
          <Strong>garage</Strong> est responsable de traitement. {ed} agit comme <Strong>sous-traitant</Strong> (article 28 du RGPD) :
          uniquement sur instruction du garage, dans le cadre d’un contrat de sous-traitance, sans utiliser ces données pour lui-même.
        </li>
      </Ul>
      <P>
        Vous êtes client d’un garage ? La notice de votre garage est accessible par le lien « Confidentialité » de votre page de suivi et de
        ses e-mails. Adressez vos demandes d’abord à votre garage.
      </P>
      <P>Les démonstrations du service n’utilisent que des données fictives.</P>

      <H2>Équipes des garages : données traitées</H2>
      <Ul>
        <li>Compte : nom, adresse e-mail, mot de passe (conservé uniquement sous une forme chiffrée irréversible).</li>
        <li>Appartenance à un garage : rôle et droits, dates d’arrivée et de retrait.</li>
        <li>Sécurité : sessions (adresse IP, navigateur, dates), invitations, demandes de nouveau mot de passe.</li>
        <li>Journal d’activité : chaque action dans un dossier, avec son auteur, sa date et l’adresse IP.</li>
        <li>Notifications sur l’appareil, si vous les activez : adresse technique d’abonnement fournie par le navigateur.</li>
      </Ul>
      <P>
        Finalités : donner accès au service et gérer les droits (exécution du contrat conclu avec le garage), sécuriser le service et garder
        la trace des actions dans les dossiers (intérêt légitime).
      </P>

      <H2>Clients des garages : ce que fait l’éditeur</H2>
      <P>Pour le compte de chaque garage, {ed} héberge les dossiers et envoie les e-mails du garage. Le service est conçu pour :</P>
      <Ul>
        <li>cloisonner les garages : un garage ne voit que ses propres données et ne peut pas savoir qu’une personne est cliente d’un autre garage ;</li>
        <li>garder les photos privées : elles ne sont servies qu’après contrôle d’accès ; les copies montrées au client ne contiennent ni position GPS ni autres métadonnées ;</li>
        <li>
          protéger la page de suivi : chaque lien personnel contient un code aléatoire, conservé uniquement sous forme d’empreinte ; il expire
          et le garage peut le révoquer ; aucune adresse ne contient de nom, de plaque ou d’autre donnée personnelle ;
        </li>
        <li>permettre au garage d’exporter ou d’effacer les données d’un client ;</li>
        <li>tracer les actions dans un journal d’activité.</li>
      </Ul>
      <P>
        L’éditeur n’accède au contenu d’un dossier pour une aide technique qu’avec l’accord préalable du garage, pour une durée limitée,
        chaque action étant tracée.
      </P>

      <H2>Prestataires (sous-traitants ultérieurs)</H2>
      <Ul>
        <li>OVH SAS : hébergement du serveur et des données, en France.</li>
        <li>
          Cloudflare, Inc. : réseau de diffusion et protection du trafic (adresses IP, requêtes). Cloudflare peut traiter ces données hors de
          l’Union européenne, notamment aux États-Unis ; ce transfert est encadré par les garanties prévues par le RGPD.
        </li>
        <li>Sendinblue SAS (Brevo), France : envoi des e-mails (adresse du destinataire et contenu de l’e-mail).</li>
        <li>Stockage externe des sauvegardes : copies chiffrées avant leur envoi.</li>
        <li>
          Services de notification des navigateurs (Google, Apple, Mozilla), pour les membres d’équipe qui activent les notifications : le
          message ne contient ni nom, ni plaque, ni montant.
        </li>
      </Ul>
      <P>Aucune donnée n’est vendue ni utilisée à des fins publicitaires.</P>

      <H2>Cookies et suivi</H2>
      <Ul>
        <li>
          Un seul cookie, strictement nécessaire : la session de l’espace garage, déposée à la connexion, inaccessible aux scripts, transmise
          uniquement en connexion chiffrée, valable {l.sessionTtlDays} jours au plus.
        </li>
        <li>La page de suivi des clients ne dépose aucun cookie.</li>
        <li>Aucune mesure d’audience, aucun cookie publicitaire, aucun service tiers de suivi dans l’application.</li>
        <li>Les e-mails ne contiennent aucun suivi des ouvertures ni des clics.</li>
      </Ul>
      <P>Ces cookies strictement nécessaires ne demandent pas de consentement.</P>

      <H2>Durées de conservation</H2>
      <Ul>
        <li>Sessions : supprimées {R.sessionDaysAfterEnd} jours après leur fin.</li>
        <li>Liens de nouveau mot de passe : supprimés {R.passwordResetHoursAfterEnd} heures après leur expiration ou leur usage ; invitations : {R.invitationDaysAfterEnd} jours après.</li>
        <li>Adresses IP du journal d’activité : effacées après {R.auditIpMonths} mois.</li>
        <li>Journal des e-mails envoyés : {R.emailDeliveryMonths} mois.</li>
        <li>Abonnements aux notifications : supprimés après {R.pushSubscriptionIdleDays} jours sans usage, à la déconnexion ou au retrait du membre.</li>
        <li>Compte d’équipe : pendant la durée de l’accès à au moins un garage, puis supprimé ou anonymisé sur demande.</li>
        <li>
          Dossiers des clients : durée fixée par chaque garage ({R.dossierYearsDefault} ans par défaut après la dernière intervention) ; preuve
          des décisions sur des travaux : {R.proofYears} ans.
        </li>
        <li>Sauvegardes chiffrées : une donnée supprimée peut y subsister jusqu’à 6 mois, sans être utilisée.</li>
      </Ul>

      <H2>Sécurité</H2>
      <P>
        Connexions chiffrées (HTTPS) ; hébergement en France ; cloisonnement des garages dans la base de données elle-même, en plus des
        contrôles de l’application ; droits vérifiés par le serveur ; mots de passe et liens conservés sous forme d’empreintes ; sauvegardes
        chiffrées hors du serveur.
      </P>

      <H2>Vos droits</H2>
      <P>
        Vous disposez d’un droit d’accès, de rectification, d’effacement, de limitation, d’opposition et de portabilité. Écrivez à {mail}.
        Réponse sous un mois.
      </P>
      <P>
        Client d’un garage : adressez-vous d’abord à votre garage, responsable de vos données. Une demande reçue par l’éditeur lui est
        transmise sans délai.
      </P>
      <P>
        Vous pouvez aussi adresser une réclamation à la CNIL (
        <a href="https://www.cnil.fr" className="font-medium text-ink underline underline-offset-2" rel="noopener noreferrer">
          www.cnil.fr
        </a>
        ).
      </P>
    </>
  );
}
